import { Navigate, Route, Routes } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { PublishProgressProvider } from "@/components/PublishProgress";
import { WSProvider } from "@/messaging/WSContext";
import { AcceptInvite } from "@/pages/AcceptInvite";
import { AdminJobs } from "@/pages/AdminJobs";
import { AdminParsers } from "@/pages/AdminParsers";
import { AdminLabels } from "@/pages/AdminLabels";
import { AnalyticsBenchmarks } from "@/pages/AnalyticsBenchmarks";
import { AnalyticsCompetition } from "@/pages/AnalyticsCompetition";
import { AnalyticsStats } from "@/pages/AnalyticsStats";
import { ClientDetail } from "@/pages/ClientDetail";
import { Clients } from "@/pages/Clients";
import { ClientShareItemView } from "@/pages/ClientShareItemView";
import { ClientShareView } from "@/pages/ClientShareView";
import { CompetitionAgency } from "@/pages/CompetitionAgency";
import { CompetitionBulk } from "@/pages/CompetitionBulk";
import { Deals } from "@/pages/Deals";
import { EmployeeDetail } from "@/pages/EmployeeDetail";
import { Finance } from "@/pages/Finance";
import { Fresh } from "@/pages/Fresh";
import { useAuth } from "@/auth/AuthContext";
import { Login } from "@/pages/Login";
import { ManualBulkPublish } from "@/pages/ManualBulkPublish";
import { MarketPublish } from "@/pages/MarketPublish";
import { AutoPublish } from "@/pages/AutoPublish";
import { OwnerFinder } from "@/pages/OwnerFinder";
import { Placements } from "@/pages/Placements";
import { Home } from "@/pages/home";
import { Leaderboard } from "@/pages/Leaderboard";
import { Profile } from "@/pages/Profile";
import { Publish } from "@/pages/Publish";
import { RolePermissions } from "@/pages/RolePermissions";
import { Schedule } from "@/pages/Schedule";
import { StreetMapping } from "@/pages/StreetMapping";
import { Team } from "@/pages/Team";
import { Vault } from "@/pages/Vault";
import { Audit } from "@/pages/Audit";
import { UserActivity } from "@/pages/UserActivity";

export default function App() {
  return (
    // WSProvider wraps PublishProgressProvider (rather than living inside
    // Layout, as it used to) so the publish-progress context can push job
    // events straight over the shared socket instead of opening one
    // EventSource per job — see PublishProgress/Context.tsx.
    <WSProvider>
    <PublishProgressProvider>
      <Routes>
      <Route path="/login" element={<Login />} />
      {/* Single-tenant lock: public agency sign-up is closed. The multi-tenant
          model is intact; new-tenant registration is just disabled. */}
      <Route path="/register" element={<Navigate to="/login" replace />} />
      <Route path="/accept-invite/:token" element={<AcceptInvite />} />
      {/* Public client-share pages — no auth, no app chrome. The token in
          the URL is the bearer of access; expired/revoked links 410 from
          the API and the page shows a friendly message. */}
      <Route path="/share/:token" element={<ClientShareView />} />
      <Route
        path="/share/:token/listings/:itemId"
        element={<ClientShareItemView />}
      />
      <Route element={<Layout />}>
        {/* Static segment outranks the ``:agencyId`` route below in the
            router's ranking, so /competition/bulk never resolves as an
            agency id. */}
        <Route path="/competition/bulk" element={<PageGuard pageKey="/competition/bulk"><CompetitionBulk /></PageGuard>} />
        <Route path="/competition/:agencyId" element={<PageGuard pageKey="/analytics/competition"><CompetitionAgency /></PageGuard>} />
        <Route path="/publish" element={<PageGuard pageKey="/publish"><Publish /></PageGuard>} />
        <Route path="/placements" element={<PageGuard pageKey="/placements"><Placements /></PageGuard>} />
        <Route path="/fresh" element={<PageGuard pageKey="/fresh"><Fresh /></PageGuard>} />
        <Route path="/owner-finder" element={<PageGuard pageKey="/owner-finder"><OwnerFinder /></PageGuard>} />
        <Route path="/market/publish" element={<PageGuard pageKey="/market/publish"><MarketPublish /></PageGuard>} />
        <Route path="/market/auto-publish" element={<PageGuard pageKey="/market/auto-publish"><AutoPublish /></PageGuard>} />
        <Route path="/publish/manual-bulk" element={<PageGuard pageKey="/publish/manual-bulk"><ManualBulkPublish /></PageGuard>} />
        <Route path="/clients" element={<PageGuard pageKey="/clients"><Clients /></PageGuard>} />
        <Route path="/clients/:clientId" element={<PageGuard pageKey="/clients"><ClientDetail /></PageGuard>} />
        <Route path="/deals" element={<PageGuard pageKey="/deals"><Deals /></PageGuard>} />
        <Route path="/finance" element={<PageGuard pageKey="/finance"><Finance /></PageGuard>} />
        <Route path="/vault" element={<PageGuard pageKey="/vault"><Vault /></PageGuard>} />
        <Route path="/leaderboard" element={<PageGuard pageKey="/leaderboard"><Leaderboard /></PageGuard>} />
        <Route path="/audit" element={<PageGuard pageKey="/audit"><Audit /></PageGuard>} />
        <Route path="/audit/users/:userId" element={<PageGuard pageKey="/audit"><UserActivity /></PageGuard>} />
        {/* /credentials lived here as its own page; the editor is now
            embedded under /profile. Redirect old links so deep-links and
            stale tabs land in the right place. */}
        <Route path="/credentials" element={<Navigate to="/profile" replace />} />
        {/* Full-page messenger removed — chat lives only in the drawer
            opened from the topbar chat icon. Old deep links and stale
            tabs land back on the dashboard; the drawer is one click
            away. */}
        <Route path="/messages" element={<Navigate to="/home" replace />} />
        <Route
          path="/messages/:conversationId"
          element={<Navigate to="/home" replace />}
        />
        {/* Home is deliberately unguarded (like /profile): it is the one
            page every role can always open, so it can serve as the universal
            landing target and PageGuard fallback. */}
        <Route path="/home" element={<Home />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/team" element={<PageGuard pageKey="/team"><Team /></PageGuard>} />
        <Route path="/team/:userId" element={<PageGuard pageKey="/team"><EmployeeDetail /></PageGuard>} />
        <Route path="/schedule" element={<PageGuard pageKey="/schedule"><Schedule /></PageGuard>} />
        {/* Old platform-admin console removed — agency-internal admin
            tools live under the "Admin" sidebar group instead. */}
        <Route path="/admin" element={<Navigate to="/home" replace />} />
        <Route path="/role-permissions" element={<PageGuard pageKey="/role-permissions"><RolePermissions /></PageGuard>} />
        <Route path="/street-mapping" element={<PageGuard pageKey="/street-mapping"><StreetMapping /></PageGuard>} />
        <Route path="/admin/jobs" element={<PageGuard pageKey="/admin/jobs"><AdminJobs /></PageGuard>} />
        <Route path="/admin/parsers" element={<PageGuard pageKey="/admin/parsers"><AdminParsers /></PageGuard>} />
        <Route path="/admin/labels" element={<PageGuard pageKey="/admin/labels"><AdminLabels /></PageGuard>} />
        <Route path="/analytics/competition" element={<PageGuard pageKey="/analytics/competition"><AnalyticsCompetition /></PageGuard>} />
        <Route path="/analytics/benchmarks" element={<PageGuard pageKey="/analytics/benchmarks"><AnalyticsBenchmarks /></PageGuard>} />
        <Route path="/analytics/stats" element={<PageGuard pageKey="/analytics/stats"><AnalyticsStats /></PageGuard>} />
        <Route path="/" element={<Navigate to="/home" replace />} />
      </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </PublishProgressProvider>
    </WSProvider>
  );
}

/** Generic per-page route guard. Reads ``user.allowed_pages`` (resolved
 *  server-side from the agency's role-page overrides) and bounces the
 *  user away from any page they aren't allowed to load. The set is
 *  recomputed on every auth refresh, so toggling a role's pages takes
 *  effect on the next page-load.
 *
 *  Fallback: ``/home`` — the personal start screen. It carries no page
 *  key and no guard, so it can never bounce the user onward and a
 *  redirect there can't loop, which the old /dashboard→/profile chain
 *  had to work around. */
function PageGuard({
  pageKey,
  children,
}: {
  pageKey: string;
  children: React.ReactNode;
}) {
  const { user } = useAuth();
  if (user && !user.allowed_pages.includes(pageKey)) {
    return <Navigate to="/home" replace />;
  }
  return <>{children}</>;
}
